import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export const ThemeContext = createContext({
  isDarkMode: false,
  setTheme: () => {},
  toggleTheme: () => {},
});

// 1. Read theme stored by the previous tab
const getInitialTheme = () => {
  if (typeof window === "undefined") return false;
  const stored = localStorage.getItem("darkMode");
  if (stored === "true") return true;
  if (stored === "false") return false;
  return false;
};

// 2. Persist to localStorage so both tabs remain in sync
const persistTheme = (isDarkMode) => {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem("darkMode", String(isDarkMode));
  } catch (e) {}
};

// 3. Apply class to <html> and <body>
const applyThemeClass = (isDarkMode) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const body = document.body;
  root.classList.toggle("dark-mode", isDarkMode);
  root.classList.toggle("dark", isDarkMode);
  root.style.colorScheme = isDarkMode ? "dark" : "light";
  if (body) {
    body.classList.toggle("dark-mode", isDarkMode);
    body.classList.toggle("dark", isDarkMode);
    body.style.colorScheme = isDarkMode ? "dark" : "light";
  }
};

// Immediately apply class before react render to avoid white flash
const initialTheme = getInitialTheme();
applyThemeClass(initialTheme);

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(getInitialTheme);

  useEffect(() => {
    applyThemeClass(isDarkMode);
    persistTheme(isDarkMode);
  }, [isDarkMode]);

  // Sync across tabs when storage event fires
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === "darkMode" && e.newValue !== null) {
        const next = e.newValue === "true";
        applyThemeClass(next);
        setIsDarkMode(next);
      }
    };
    const handleThemeReset = () => setIsDarkMode(false);
    window.addEventListener("storage", handleStorage);
    window.addEventListener("theme:reset", handleThemeReset);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("theme:reset", handleThemeReset);
    };
  }, []);

  const setTheme = useCallback((value) => {
    const next = Boolean(value);
    applyThemeClass(next);
    persistTheme(next);
    setIsDarkMode(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setIsDarkMode((prev) => {
      const next = !prev;
      applyThemeClass(next);
      persistTheme(next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ isDarkMode, setTheme, toggleTheme }),
    [isDarkMode, setTheme, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
export default ThemeContext;

